import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { getToken } from '../../lib/auth'
import { updateContentPreferences } from '../../api/contentPreferences'

type Warning = {
  type: 'nudity' | 'violence' | 'sensitive'
  message: string
  viewer_allowed?: boolean
  blur_preview?: boolean
}

type Props = {
  imageUrl?: string | null
  alt: string
  warning?: Warning | null
  className?: string
}

export default function SensitiveMedia({ imageUrl, alt, warning, className }: Props) {
  const authed = Boolean(getToken())
  const [localAllowed, setLocalAllowed] = useState(false)
  const [busy, setBusy] = useState(false)
  const [revealed, setRevealed] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const allowed = Boolean(localAllowed || warning?.viewer_allowed)
  const showBlockedCard = Boolean(warning && !allowed)
  const canRenderImage = Boolean(imageUrl && (!showBlockedCard || localAllowed))
  const shouldBlur = Boolean(canRenderImage && warning?.blur_preview && !revealed)
  const title = useMemo(() => {
    if (!warning) return ''
    return warning.type === 'violence' ? 'Sensitive content' : 'Sensitive content'
  }, [warning])

  async function onAllowAndView() {
    if (!warning || !authed) return
    setBusy(true)
    setErr(null)
    try {
      const payload: any = { age_confirmed_18: true }
      if (warning.type === 'nudity') payload.allow_sensitive_nudity = true
      if (warning.type === 'violence') payload.allow_sensitive_violence = true
      if (warning.type === 'sensitive') {
        payload.allow_sensitive_nudity = true
        payload.allow_sensitive_violence = true
      }
      await updateContentPreferences(payload)
      setLocalAllowed(true)
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.message || 'Could not update settings')
    } finally {
      setBusy(false)
    }
  }

  async function onConfirm18() {
    if (!authed) return
    setBusy(true)
    setErr(null)
    try {
      await updateContentPreferences({ age_confirmed_18: true })
      setLocalAllowed(true)
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.message || 'Could not confirm age')
    } finally {
      setBusy(false)
    }
  }

  if (canRenderImage) {
    return (
      <div className={`relative ${className || ''}`}>
        <img src={String(imageUrl)} alt={alt} className={`h-full w-full object-cover ${shouldBlur ? 'blur-xl' : ''}`} />
        {shouldBlur ? (
          <button
            type="button"
            className="absolute inset-0 m-auto h-fit w-fit rounded-full border border-white/20 bg-black/60 px-4 py-2 text-sm text-white"
            onClick={() => setRevealed(true)}
          >
            Tap to reveal
          </button>
        ) : null}
      </div>
    )
  }

  if (!showBlockedCard) {
    return <div className={`bg-white/5 ${className || ''}`} />
  }

  return (
    <div className={`flex h-full w-full items-center justify-center bg-white/5 p-4 text-center ${className || ''}`}>
      <div className="max-w-sm space-y-2">
        <div className="text-base font-semibold">{title}</div>
        <div className="text-sm text-white/80">{warning?.message || 'This content is sensitive.'}</div>
        {!authed ? (
          <div className="text-xs text-white/70">
            Sign in to adjust your content settings. <Link to="/login" className="underline">Sign in</Link>
          </div>
        ) : (
          <div className="flex flex-wrap items-center justify-center gap-2 pt-1">
            <button type="button" onClick={onConfirm18} disabled={busy} className="rounded-lg border border-white/20 px-3 py-1.5 text-xs">
              Continue (18+)
            </button>
            <button type="button" onClick={onAllowAndView} disabled={busy} className="rounded-lg bg-brand px-3 py-1.5 text-xs font-semibold text-white">
              Allow & View
            </button>
          </div>
        )}
        {err ? <div className="text-xs text-red-300">{err}</div> : null}
      </div>
    </div>
  )
}
